import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState
} from 'react'

import { Toast } from '@/components/common/UI/Toast'

export type NotificationType = 'success' | 'error' | 'warning' | 'info'

interface Notification {
    id: string
    message: string
    type: NotificationType
    duration?: number
}

export interface NotificationContextType {
    notifications: Notification[]
    showNotification: (
        message: string,
        type: NotificationType,
        duration?: number
    ) => void
    removeNotification: (id: string) => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(
    undefined
)

export function NotificationProvider({
    children
}: Readonly<React.PropsWithChildren>): JSX.Element {
    const [notifications, setNotifications] = useState<Notification[]>([])

    const showNotification = useCallback(
        (message: string, type: NotificationType, duration = 5000): void => {
            const id = Math.random().toString(36).substring(7)
            const notification = { id, message, type, duration }

            setNotifications((prev) => [...prev, notification])

            if (duration > 0) {
                setTimeout(() => {
                    removeNotification(id)
                }, duration)
            }
        },
        []
    )

    const removeNotification = useCallback((id: string) => {
        setNotifications((prev) =>
            prev.filter((notification) => notification.id !== id)
        )
    }, [])

    const value = useMemo(
        () => ({ notifications, showNotification, removeNotification }),
        [notifications, showNotification, removeNotification]
    )

    return (
        <NotificationContext.Provider value={value}>
            {children}
            <div className="fixed bottom-4 right-4 space-y-2 z-50">
                {notifications.map((notification) => (
                    <Toast
                        key={notification.id}
                        message={notification.message}
                        type={notification.type}
                        onClose={() => removeNotification(notification.id)}
                    />
                ))}
            </div>
        </NotificationContext.Provider>
    )
}

export const useNotification = () => {
    const context = useContext(NotificationContext)

    if (!context) {
        throw new Error(
            'useNotification must be used within a NotificationProvider'
        )
    }

    return context
}
export const toast = {
    success: (message: string) => {
        return (
            showNotification: (message: string, type: NotificationType) => void
        ) => {
            showNotification(message, 'success')
        }
    },
    error: (message: string) => {
        return (
            showNotification: (message: string, type: NotificationType) => void
        ) => {
            showNotification(message, 'error')
        }
    },
    warning: (message: string) => {
        return (
            showNotification: (message: string, type: NotificationType) => void
        ) => {
            showNotification(message, 'warning')
        }
    },
    info: (message: string) => {
        return (
            showNotification: (message: string, type: NotificationType) => void
        ) => {
            showNotification(message, 'info')
        }
    }
}
